import { forwardRef } from "react";
import { NodeViewComponentProps } from "@handlewithcare/react-prosemirror";
import useSingleContent from "@/content/useSingleContentQuery/useSingleContentQuery";
import ContentRow from "@/components/ContentRow";
import ContentCardV2 from "@/components/ContentCardV2";
import { ContentRowProps } from "@/components/ContentRow/ContentRow.interface";
import { CardViewmodelAndProps } from "@/components/ContentCardV2/ContentCardV2.interface";

type ViewmodelProps = {
  flavor: string;
};

const Viewmodel = forwardRef<
  HTMLDivElement,
  NodeViewComponentProps & ViewmodelProps
>(({ nodeProps, flavor }, ref) => {
  const nodeType = nodeProps.node.attrs["type"];
  const nodeId = nodeProps.node.attrs["id"];

  const { data, isLoading } = useSingleContent<
    ContentRowProps | CardViewmodelAndProps
  >({
    contentId: nodeId,
    queryParams: { viewmodel_type: nodeType },
  });

  return (
    /**
     * This outer <div ref={ref}> is the *nodeview root* that ProseMirror
     * attaches to the document. ProseMirror relies on this stable root to
     * map between DOM and document positions. It must:
     *   - Always be present (even if contents are empty)
     *   - Always have the `ref` attached
     */
    <div
      ref={ref}
      onMouseDownCapture={(e) => {
        // Prevent ProseMirror from interpreting clicks on this node’s UI
        // as selection events. Especially important once viewmodels become
        // draggable/editable, since those interactions should be handled by
        // our own logic, not by PM’s selection system.
        e.stopPropagation();
      }}
    >
      {!isLoading && !data && null}

      {!isLoading && data && (
        <>
          {nodeType === "row" && (
            <ContentRow {...(data as ContentRowProps)} flavor={flavor} />
          )}
          {nodeType === "card" && (
            <ContentCardV2 {...(data as CardViewmodelAndProps)} />
          )}
        </>
      )}
    </div>
  );
});

export default Viewmodel;
